// Connectors family on NomOS (SEN-395 / TZ-2): Sentinel's unified connector API (/connectors) and the
// legacy per-provider /integrations methods, both on NomOS's connections API (Rhizome/Kerberos):
//   providers            GET  /providers                     (manifests: config_schema, display, capabilities)
//   connections          GET/POST/PATCH/DELETE /connections  (+ PUT /connections/{id}/credential)
//   login check/choices  POST /providers/{p}/validate, GET /providers/{p}/options/{field}
//   grants               POST /connections/{id}/grants       (the person's own access: read, map, load)
//   discover             POST/GET /connections/{id}/discover (synchronous; the inbox)
//   link                 /connections/{id}/mapping           (the Map review draft -> ONE proposed change)
//   sync                 /connections/{id}/loads[/plan]      (a schedule request -> ONE proposed change)
// Owner ruling: a person's own edits apply immediately. NomOS routes a mapping and a load schedule through a
// proposed change; for the person acting here the impl approves that change in the same call
// (POST /proposed-changes/{id}/approve, human admin session), so nothing waits. Agents never use this path.
// The provider-specific methods are thin wrappers that pick the connector key and reshape.
// Credentials: sent only by create / update-credentials / validate, never read back, never logged.
// Reshape only: counts come from NomOS (loads status `meters`); nothing is aggregated from readings.
import type {
  DatadisConnectionCreate, DatadisConnectionListResponse, DatadisConnectionResponse, DatadisValidateRequest,
  DatadisValidateResponse, EforsyningConnectionCreate, EforsyningConnectionListResponse,
  EforsyningConnectionResponse, EforsyningUtilitiesResponse, EforsyningValidateResponse, ElkraftConnection,
  ElkraftConnectionCreate, ElkraftConnectionListResponse, ElkraftValidateRequest, ElkraftValidateResponse,
  KmdConnectionCreate, KmdConnectionListResponse, KmdConnectionResponse, KmdPlantsResponse, KmdValidateRequest,
  KmdValidateResponse, NoovaConnectionCreate, NoovaConnectionListResponse, NoovaConnectionResponse,
  NoovaValidateResponse, ShellyCredentialsConnectionCreate,
} from '@/types';
import type {
  AuthorizationOut, ConnectionCreateBody, ConnectionOut, ConnectorInfo, ConsentRequestCreate, ConsentRequestCreated,
  ConsentRequestOut, ConsentStatus, PlatformCredentialOut, CredentialField, DiscoverRunOut,
  DiscoverStarted, DiscoveredItem, ExtraColumn, IdentityOut, IdentityStatus, ItemKind, ItemStatus,
  ItemSyncStatus, LinkItemRequest, LinkResponse, LinkResult, OptionItem, Page, SyncJob, SyncRequestBody,
  SyncStatusOut, ValidateResult,
} from '@/types/connectors';
import { SYNC_JOB_QUEUED } from '@/types/connectors';
import { attributeIds, getConfig, getMe, nomosFetch, queryRecords, scalar, toAxiosError, zulu, type NomosEnvelope } from '../client';
import { rememberId, resolveRecordIds } from '../ids';
import type { AnyFn, NomosRegistry } from '../registry';
import { NomosGapError } from './fieldops';

// ---------------------------------------------------------------- the Sentinel connector registry on NomOS
interface Spec {
  key: string;                 // Sentinel connector key
  providers: string[];         // NomOS provider profiles it covers; [0] is the one a new login creates
  account: string[];           // fields that make the provider account_id (carry-over convention)
  required: string[];
  hidden?: string[];           // config fields the form does not ask for
  options?: string[];          // fields whose choices come from getConnectorOptions
  /** a form field that is only the account name (not provider config) */
  accountField?: { name: string; label: string; type: CredentialField['type'] };
  cardinality: ConnectorInfo['login_cardinality'];
  kinds: ItemKind[];
  loginFlow?: ConnectorInfo['login_flow'];
  renew: number;
}

const SPECS: Spec[] = [
  { key: 'datadis', providers: ['datadis.es'], account: ['username'], required: ['username', 'password'], cardinality: '1:1', kinds: ['meter'], renew: 30 },
  { key: 'eforsyning', providers: ['eforsyning.dk'], account: ['supplier', 'consumer_number'], required: ['supplier', 'consumer_number', 'pin_code'],
    options: ['supplier'], hidden: ['label'], cardinality: 'N:1', kinds: ['meter'], renew: 30 },
  { key: 'kmd_minforsyning', providers: ['kmd.dk'], account: ['email', 'plant', 'utility'], required: ['plant', 'email', 'password'],
    options: ['plant'], hidden: ['utility'], cardinality: 'N:1', kinds: ['meter'], renew: 30 },
  { key: 'eloverblik', providers: ['eloverblik.thirdparty', 'eloverblik.customer'], account: ['customer_cvr'], required: [],
    cardinality: '1:1', kinds: ['meter'], loginFlow: 'consent_link', renew: 60 },
  { key: 'elkraft', providers: ['elkraft.se'], account: ['username'], required: ['username', 'password'], cardinality: '1:1', kinds: ['meter'], renew: 30 },
  { key: 'noova', providers: ['noova.no'], account: ['username'], required: ['customer_id', 'username', 'password'], cardinality: '1:1', kinds: ['meter'], renew: 30 },
  { key: 'ista', providers: ['ista.dk'], account: ['username'], required: ['username', 'password'], cardinality: 'N:1', kinds: ['meter'], renew: 30 },
  { key: 'shelly', providers: ['shelly.cloud'], account: ['account'], required: ['account', 'api_key'], cardinality: 'N:1', kinds: ['meter'],
    accountField: { name: 'account', label: 'Shelly account (e-mail)', type: 'email' }, renew: 30 },
];
const BY_KEY = new Map(SPECS.map((s) => [s.key, s]));
const BY_PROVIDER = new Map(SPECS.flatMap((s) => s.providers.map((p) => [p, s] as const)));

/** The Sentinel connector a NomOS provider profile belongs to (the kept consent wizard is keyed by it). */
export function connectorForProvider(provider: string): string | null {
  return BY_PROVIDER.get(provider)?.key ?? null;
}

function spec(connector: string): Spec {
  const s = BY_KEY.get(connector);
  if (!s) {
    if (connector === 'techem') throw new NomosGapError('connectors: Techem has no NomOS provider package (monad/rhizome/providers has no techem)');
    throw toAxiosError(404, { detail: `Unknown connector: ${connector}` }, '');
  }
  return s;
}

// ---------------------------------------------------------------- NomOS wire shapes
interface NField { key: string; kind: string; label: string; default: unknown; choices: Array<{ value?: string; id?: string; label?: string }> | null }
interface NDisplayField { source_field: string; label: string; summary: boolean }
interface NProvider {
  key: string; family: string; title: string; country: string | null; auth_kind: string; config_schema: NField[];
  metadata_discovery: Array<{ display_groups?: Array<{ key: string; label: string; fields: NDisplayField[] }> }>;
  implemented: boolean;
}
interface NConnection {
  id: string; provider: string; account_id: string; name: string; config: Record<string, unknown>;
  credential_configured: boolean; revision: number; created_at: string; updated_at: string;
  freshness?: string; last_run_at?: string | null; next_run_at?: string | null;
}
interface NUse { id: string; principal_id: string; expires_at: string; access?: { held: string[] } }
interface NEntry { connection: NConnection; uses: NUse[]; access: Array<{ key: string; available: boolean }> }
interface NItem {
  id: string; source_kind: string; source_id: string; target_record_id: string | null; absent: boolean;
  status: 'not_seen' | 'ignored' | 'applied' | 'review'; match_state: 'bound' | 'suggested' | 'new';
  proposed_values: Record<string, unknown>; source_display: Record<string, unknown>; last_seen_at?: string;
}
interface NApproval {
  request_id: string; status: string; intent: { connection_id: string; backfill_from?: string };
  created_at?: string; decided_at?: string | null; proposed_change_id?: string | null;
}
interface NLoads {
  approval: NApproval | null; pending: NApproval | null; latest: NApproval | null; blocked: string | null;
  meters: number; approved_meters: number; loaded_meters: number; readings_until: string | null;
  errors: Array<{ identity: string; code: string; provider_text?: string | null }>;
  history_available_from: string | null;
}

const data = <T>(r: NomosEnvelope<T>): T => r.data;
const enc = encodeURIComponent;

// ---------------------------------------------------------------- providers
let providersP: Promise<NProvider[]> | null = null;
function providers(): Promise<NProvider[]> {
  if (!providersP) {
    providersP = nomosFetch<NomosEnvelope<{ providers: NProvider[] }>>('GET', '/providers').then((r) => r.data.providers);
    providersP.catch(() => { providersP = null; });
  }
  return providersP;
}
export function clearConnectorCaches(): void { providersP = null; meP = null; }

async function manifest(s: Spec, provider = s.providers[0]): Promise<NProvider> {
  const m = (await providers()).find((p) => p.key === provider);
  if (!m) throw new NomosGapError(`connectors: NomOS serves no provider profile ${provider} for ${s.key}`);
  return m;
}

function fieldType(f: NField, s: Spec): CredentialField['type'] {
  if (f.kind === 'secret' || f.kind === 'oauth') return 'password';
  if (s.options?.includes(f.key) || f.choices) return 'select';
  if (f.key.includes('email')) return 'email';
  return f.kind === 'number' ? 'number' : 'text';
}

function credentialFields(s: Spec, m: NProvider): CredentialField[] {
  if (s.loginFlow === 'consent_link') {
    // Sentinel's consent login: the id of a received consent request (ConsentStep fills it).
    return [{ name: 'consent_request_id', label: 'Consent request', type: 'text', required: true, secret: false, identity: false,
      options_field: null, help: 'The id of a received consent request (createConsentRequest).' }];
  }
  const out: CredentialField[] = [];
  if (s.accountField) out.push({ ...s.accountField, required: true, secret: false, identity: true, options_field: null, help: null });
  for (const f of m.config_schema) {
    if (s.hidden?.includes(f.key)) continue;
    const secret = f.kind === 'secret' || f.kind === 'oauth';
    out.push({ name: f.key, label: f.label, type: fieldType(f, s), required: s.required.includes(f.key), secret,
      identity: !secret && s.account.includes(f.key), options_field: s.options?.includes(f.key) ? f.key : null, help: null });
  }
  // Sentinel's order: identity first, then the secret, then the optional rest.
  const rank = (f: CredentialField) => (f.identity ? 0 : f.secret ? 1 : f.required ? 2 : 3);
  return out.sort((a, b) => rank(a) - rank(b));
}

const ADDRESS_FIELDS = new Set(['address', 'street', 'streetName', 'postalCode', 'postcode', 'postal_code', 'zip', 'municipality', 'city', 'town']);
function extraColumns(s: Spec, m: NProvider): ExtraColumn[] {
  const cols: ExtraColumn[] = [];
  for (const d of m.metadata_discovery ?? []) for (const g of d.display_groups ?? []) for (const f of g.fields) {
    if (ADDRESS_FIELDS.has(f.source_field) || cols.some((c) => c.key === f.source_field)) continue;
    cols.push({ key: f.source_field, label: f.label, kinds: ['meter'], editable: false, options_field: null });
  }
  if (s.key === 'shelly') cols.push({ key: 'seu', label: 'SEU', kinds: ['meter'], editable: true, options_field: 'seu' });
  return cols;
}

async function getConnectors(): Promise<ConnectorInfo[]> {
  const ps = await providers();
  const out: ConnectorInfo[] = [];
  for (const s of SPECS) {
    const m = ps.find((p) => p.key === s.providers[0] && p.implemented);
    if (!m) continue;
    out.push({ key: s.key, label: m.title, country: m.country, credential_fields: credentialFields(s, m),
      login_cardinality: s.cardinality, kinds: s.kinds, extra_columns: extraColumns(s, m),
      login_flow: s.loginFlow ?? 'credentials', renew_within_days: s.renew });
  }
  return out;
}

async function getConnectorOptions(connector: string, field: string): Promise<OptionItem[]> {
  const s = spec(connector);
  if (s.key === 'shelly' && field === 'seu') {
    // The meter's SEU category is a reference to seu_category records (code, name).
    return [...(await seuCodes()).values()].map((v) => ({ value: v.code, label: v.name, extra: {} })).sort((a, b) => a.label.localeCompare(b.label));
  }
  const r = await nomosFetch<NomosEnvelope<{ options: OptionItem[] }>>('GET', `/providers/${enc(s.providers[0])}/options/${enc(field)}`);
  return r.data.options;
}

// ---------------------------------------------------------------- credentials -> NomOS account, config, secret
function str(v: unknown): string { return v === undefined || v === null ? '' : String(v).trim(); }

/** Split a Sentinel credentials form into NomOS's (account_id, config, credential). Never logs. */
function splitCredentials(s: Spec, m: NProvider, creds: Record<string, unknown>) {
  const values: Record<string, string> = {};
  for (const [k, v] of Object.entries(creds)) values[k] = str(v);
  if (s.key === 'kmd_minforsyning' && values.plant?.includes(':')) {
    // Sentinel's plant choice is "<plant>:<utility>"; NomOS keeps the two apart (utility 0 = none).
    const [plant, utility] = values.plant.split(':');
    values.plant = plant;
    if (utility && utility !== '0') values.utility = utility;
  }
  const secretField = m.config_schema.find((f) => f.kind === 'secret' || f.kind === 'oauth');
  const credential = secretField ? values[secretField.key] ?? '' : '';
  const config: Record<string, string> = {};
  for (const f of m.config_schema) {
    if (f === secretField) continue;
    if (values[f.key]) config[f.key] = values[f.key];
  }
  const account = s.account.map((k) => values[k]).filter(Boolean).join('/');
  return { account, config, credential, secretKey: secretField?.key };
}

// ---------------------------------------------------------------- connections
async function entries(connectionId?: string): Promise<NEntry[]> {
  return data(await nomosFetch<NomosEnvelope<NEntry[]>>('GET', '/connections', undefined,
    connectionId ? { query: { connection_id: connectionId } } : {}));
}

async function entry(connector: string, connectionId: string): Promise<NEntry> {
  const s = spec(connector);
  const e = (await entries(connectionId))[0];
  if (!e || !s.providers.includes(e.connection.provider)) throw toAxiosError(404, { detail: 'Connection not found' }, '');
  return e;
}

/** The connection's load status: what NomOS counts for it (meters) and its schedule. Admin read. */
async function loads(connectionId: string): Promise<NLoads> {
  return data(await nomosFetch<NomosEnvelope<NLoads>>('GET', `/connections/${enc(connectionId)}/loads`));
}

function status(c: NConnection): string {
  return c.credential_configured ? 'active' : 'reauth_required';
}

function connectionOut(c: NConnection, l: NLoads | null): ConnectionOut {
  const s = BY_PROVIDER.get(c.provider)!;
  const m = (c.config ?? {}) as Record<string, unknown>;
  return {
    id: c.id, connector: s.key, name: c.name, status: status(c),
    // NomOS: one connection holds exactly one login, so the login is the connection.
    connection_id: c.id, identity_id: c.id,
    ...(l ? { item_counts: { meter: l.meters } } : {}),
    last_sync_at: zulu(c.last_run_at ?? null),
    last_error: l?.blocked ? { code: l.blocked, message: l.blocked } : null,
    created_at: zulu(c.created_at),
    credentials_expires_at: null, credentials_expire_in_days: null, credentials_expiring: false,
    extra: { ...m, provider: c.provider, freshness: c.freshness ?? null },
  };
}

/** Every connection's load status in ONE call (`POST /connection-loads/query`, SEN-395 be-perf; it was one call per
 *  connection). A connection NomOS refuses (or a non-admin caller) gets null, as the per-connection read did. */
async function withLoads(list: NConnection[]): Promise<Array<[NConnection, NLoads | null]>> {
  const chunks: string[][] = [];
  for (let i = 0; i < list.length; i += 200) chunks.push(list.slice(i, i + 200).map((c) => c.id));   // the endpoint's cap
  const got = await Promise.all(chunks.map((ids) => nomosFetch<NomosEnvelope<Array<{ connection_id: string; loads?: NLoads }>>>(
    'POST', '/connection-loads/query', { connection_ids: ids }).then((r) => r.data).catch(() => [])));
  const byId = new Map(got.flat().map((x) => [x.connection_id, x.loads ?? null]));
  return list.map((c) => [c, byId.get(c.id) ?? null] as [NConnection, NLoads | null]);
}

function page<T>(items: T[], params?: { limit?: number; cursor?: string }): Page<T> {
  const offset = params?.cursor ? Number(params.cursor) : 0;
  const limit = params?.limit ?? 50;
  const slice = items.slice(offset, offset + limit);
  return { items: slice, next_cursor: offset + limit < items.length ? String(offset + limit) : null, total: items.length };
}

async function connectionsOf(connector?: string): Promise<NConnection[]> {
  const all = (await entries()).map((e) => e.connection).filter((c) => BY_PROVIDER.has(c.provider));
  return connector ? all.filter((c) => spec(connector).providers.includes(c.provider)) : all;
}

async function getConnectorConnection(connector: string, connectionId: string): Promise<ConnectionOut> {
  const e = await entry(connector, connectionId);
  return connectionOut(e.connection, await loads(connectionId).catch(() => null));
}

async function validateConnectorCredentials(connector: string, credentials: Record<string, unknown>): Promise<ValidateResult> {
  const s = spec(connector);
  if (s.loginFlow === 'consent_link') {
    const id = str(credentials.consent_request_id);
    if (!id) return { valid: false, error: 'Eloverblik connects through a consent request', details: {} };
    const req = await getConsentRequest(connector, id);
    return req.status === 'received' ? { valid: true, error: null, details: {} } : { valid: false, error: 'This consent is not received (yet)', details: { status: req.status } };
  }
  const m = await manifest(s);
  const { account, config, credential } = splitCredentials(s, m, credentials);
  try {
    const r = await nomosFetch<NomosEnvelope<{ valid: boolean; code: string | null }>>(
      'POST', `/providers/${enc(m.key)}/validate`, { account_id: account || m.key, config, credential });
    return { valid: r.data.valid, error: r.data.valid ? null : `${m.title} refused the login (${r.data.code})`, details: { code: r.data.code } };
  } catch (e) {
    // Eloverblik and Shelly have no separate login step on NomOS: the key is checked by the first discovery,
    // which the wizard runs right after saving. Say so instead of claiming a check that did not happen.
    const code = (e as { response?: { data?: { error?: { code?: string } } } }).response?.data?.error?.code;
    if (code === 'unavailable_operation') return { valid: true, error: null, details: { checked: false, checked_by: 'first_discovery' } };
    throw e;
  }
}

async function createConnectorConnection(connector: string, body: ConnectionCreateBody): Promise<ConnectionOut> {
  const s = spec(connector);
  if (body.identity_id) throw new NomosGapError('connectors: restoring an archived login is not possible on NomOS (archiving a connection deletes its credential)');
  if (s.loginFlow === 'consent_link') return connectFromConsent(body.credentials ?? {}, body.name ?? null);
  const m = await manifest(s);
  const { account, config, credential } = splitCredentials(s, m, body.credentials ?? {});
  if (!account) throw toAxiosError(422, { detail: `${s.account.join(' and ')} required` }, '');
  if (!credential) throw toAxiosError(422, { detail: 'The password or key is required' }, '');
  const name = str(body.name) || `${m.title} ${account}`.slice(0, 120);
  const created = data(await nomosFetch<NomosEnvelope<NConnection>>('POST', '/connections',
    { provider: m.key, account_id: account, name, config }));
  await nomosFetch('PUT', `/connections/${enc(created.id)}/credential`, { credential });
  return connectionOut({ ...created, credential_configured: true }, null);
}

async function renameConnectorConnection(connector: string, connectionId: string, name: string): Promise<ConnectionOut> {
  await entry(connector, connectionId);
  await nomosFetch('PATCH', `/connections/${enc(connectionId)}`, { name });
  return getConnectorConnection(connector, connectionId);
}

async function listConnectorIdentities(connector: string, params?: { status?: IdentityStatus; limit?: number; cursor?: string }): Promise<Page<IdentityOut>> {
  const s = spec(connector);
  if (params?.status === 'archived') throw new NomosGapError('connectors: NomOS lists no archived logins (archiving a connection deletes its credential; nothing is restorable)');
  const rows = (await connectionsOf(connector)).map((c): IdentityOut => ({
    id: c.id, connector: s.key, label: c.name, status: c.credential_configured ? 'active' : 'reauth_required',
    connection_id: c.id, created_at: zulu(c.created_at), archived_at: null,
    fields: Object.fromEntries(s.account.filter((k) => k in (c.config ?? {})).map((k) => [k, c.config[k]])),
  }));
  return page(params?.status ? rows.filter((r) => r.status === params.status) : rows, params);
}


async function updateConnectorCredentials(connector: string, connectionId: string, credentials: Record<string, unknown>): Promise<void> {
  const s = spec(connector);
  const e = await entry(connector, connectionId);
  if (s.loginFlow === 'consent_link') { await connectFromConsent(credentials, null, connectionId); return; }
  const m = await manifest(s, e.connection.provider);
  const { config, credential } = splitCredentials(s, m, credentials);
  if (!credential) throw toAxiosError(422, { detail: 'The password or key is required' }, '');
  const changed = Object.entries(config).filter(([k, v]) => str(e.connection.config?.[k]) !== v).map(([k]) => k);
  if (changed.length) throw new NomosGapError(`connectors: NomOS cannot change a connection's ${changed.join(', ')} (only its credential); create a new connection`);
  await nomosFetch('PUT', `/connections/${enc(connectionId)}/credential`, { credential });
}

// ---------------------------------------------------------------- the person's own grant
let meP: Promise<string> | null = null;
function me(): Promise<string> {
  if (!meP) { meP = getMe().then((m) => String(m.member_id)); meP.catch(() => { meP = null; }); }
  return meP;
}

const GRANT_DAYS = 365;
/** A grant held by the person on this connection with (at least) `wanted`, made when there is none. */
async function grant(connectionId: string, wanted: string[]): Promise<string> {
  const [member, e] = await Promise.all([me(), entries(connectionId).then((r) => r[0])]);
  if (!e) throw toAxiosError(404, { detail: 'Connection not found' }, '');
  const offered = new Set(e.access.filter((a) => a.available).map((a) => a.key));
  const need = wanted.filter((k) => offered.has(k));
  if (!need.includes('read_meters')) throw toAxiosError(409, { detail: 'This connection offers no meter reads', error: { code: 'capability_unavailable' } }, '');
  const soon = Date.now() + 24 * 3600 * 1000;
  const held = e.uses.find((u) => u.principal_id === member && Date.parse(u.expires_at) > soon && need.every((k) => u.access?.held.includes(k)));
  if (held) return held.id;
  const created = data(await nomosFetch<NomosEnvelope<NUse>>('POST', `/connections/${enc(connectionId)}/grants`, {
    principal_id: member, name: 'Sentinel: connect meters', capabilities: need,
    expires_at: new Date(Date.now() + GRANT_DAYS * 24 * 3600 * 1000).toISOString(),
  }));
  return created.id;
}

/** A person's own change applies at once: approve the proposed change NomOS made for it. */
async function approve(proposedChangeId: string | null | undefined): Promise<void> {
  if (!proposedChangeId) throw toAxiosError(409, { detail: 'NomOS recorded no proposed change to approve' }, '');
  await nomosFetch('POST', `/proposed-changes/${enc(proposedChangeId)}/approve`, {});
}

// ---------------------------------------------------------------- discover
/**
 * What a person can do when the provider refused a discovery's fetch: NomOS answers 422
 * `metadata_fetch_refused` / `provider_fetch_not_completed` with the provider's closed refusal code
 * in `details.cause`. A known cause becomes a sentence naming the provider; anything else is unchanged.
 */
const PROVIDER_CAUSES: Record<string, (provider: string) => string> = {
  provider_authentication_failed: (p) => `${p} didn't accept the sign-in. Check the connection's username and password.`,
  credential_not_configured: (p) => `This ${p} connection has no stored sign-in. Enter its username and password again.`,
  credential_unavailable: (p) => `This ${p} connection's stored sign-in could not be read. Enter its username and password again.`,
  provider_access_denied: (p) => `${p} accepted the sign-in but refused access. Check the account's permissions at ${p}.`,
  provider_configuration_invalid: (p) => `${p} refused the connection's settings. Check them on the connection.`,
  provider_rate_limited: (p) => `${p} is limiting requests right now. Try again in a few minutes.`,
  provider_timeout: (p) => `${p} did not answer in time. Try again in a few minutes.`,
  provider_transport_error: (p) => `${p} could not be reached. Try again in a few minutes.`,
  provider_request_refused: (p) => `${p} refused the request NomOS sent. Retrying will not help; please report it to support.`,
};
const FETCH_REFUSALS = new Set(['metadata_fetch_refused', 'provider_fetch_not_completed']);

export async function humanProviderRefusal(e: unknown, connector: string, provider?: string): Promise<unknown> {
  const r = (e as AxiosLike)?.response;
  const error = r?.data?.error;
  const cause = error?.details?.cause;
  const say = error?.code && FETCH_REFUSALS.has(error.code) && typeof cause === 'string' ? PROVIDER_CAUSES[cause] : undefined;
  if (!say || !r) return e;
  let title = connector;
  try { const s = spec(connector); title = (await manifest(s, provider ?? s.providers[0])).title; } catch { /* keep the key */ }
  const message = say(title);
  return toAxiosError(r.status, { detail: message, error: { ...error, message } }, r.config?.url ?? '');
}
type AxiosLike = { response?: { status: number; config?: { url?: string };
  data?: { error?: { code?: string; message?: string; details?: { cause?: unknown } } } } };

async function discoverNow(connector: string, connectionId: string, useId: string, provider?: string): Promise<void> {
  try {
    await nomosFetch('POST', `/connections/${enc(connectionId)}/discover`, { use_id: useId });
  } catch (e) {
    throw await humanProviderRefusal(e, connector, provider);
  }
}

async function inbox(connectionId: string, useId: string): Promise<NItem[]> {
  return data(await nomosFetch<NomosEnvelope<{ items: NItem[] }>>('GET', `/connections/${enc(connectionId)}/discover`, undefined,
    { query: { use_id: useId } })).items ?? [];
}

async function startConnectorDiscover(connector: string, connectionId: string): Promise<DiscoverStarted> {
  const { connection } = await entry(connector, connectionId);
  const useId = await grant(connectionId, ['read_meters', 'map_meter_data']);
  await discoverNow(connector, connectionId, useId, connection.provider);
  // NomOS discovers synchronously; the "run" is the person's grant, whose inbox the run reads.
  return { run_id: useId, status: 'completed' };
}

function valuesBy(cells: Record<string, Record<string, unknown>>, slugOf: Record<string, string>) {
  return Object.fromEntries(Object.entries(cells).map(([rid, c]) => [rid,
    Object.fromEntries(Object.entries(c).map(([aid, v]) => [slugOf[aid] ?? aid, v]))]));
}

async function recordValues(type: string, ids: string[], slugs: string[]): Promise<Record<string, Record<string, unknown>>> {
  if (!ids.length) return {};
  const [vr, attrs] = await Promise.all([
    nomosFetch<NomosEnvelope<{ values?: Record<string, Record<string, unknown>> }>>('POST', `/objects/${type}/records/values`, { record_ids: ids, attributes: slugs }),
    attributeIds(type),
  ]);
  const slugOf = Object.fromEntries(Object.entries(attrs).map(([s, id]) => [id, s]));
  return valuesBy(vr.data?.values ?? {}, slugOf);
}

const ref = (v: unknown): string | null => {
  const x = Array.isArray(v) ? v[0] : v;
  return x && typeof x === 'object' && 'target_record_id' in (x as object) ? String((x as { target_record_id: string }).target_record_id) : null;
};
const first = (o: Record<string, unknown>, keys: string[]) => { for (const k of keys) if (o[k] !== undefined && o[k] !== null && o[k] !== '') return String(o[k]); return null; };

async function seuCodes(): Promise<Map<string, { code: string; name: string }>> { // record id -> code, name
  const r = await queryRecords('seu_category', { fields: ['code', 'name'], limit: 200 });
  return new Map(r.rows.map((x) => [x.record_id, { code: String(scalar(x.values.code) ?? ''), name: String(scalar(x.values.name) ?? x.kenning) }]));
}

async function energyCodes(): Promise<Map<string, string>> { // record id -> code
  const r = await queryRecords('energy_source', { fields: ['code'], limit: 200 });
  return new Map(r.rows.map((x) => [x.record_id, String(scalar(x.values.code) ?? '')]));
}

/** Inbox items as Sentinel's DiscoveredItem, with the bound meter's Sentinel ids and building. */
async function discovered(items: NItem[]): Promise<DiscoveredItem[]> {
  const meterAttrs = await attributeIds('meter');
  const nameA = meterAttrs.name, siteA = meterAttrs.site, energyA = meterAttrs.energy_source;
  const bound = items.map((i) => i.target_record_id).filter((x): x is string => !!x);
  const meters = await recordValues('meter', bound, ['sentinel_meter_id', 'site', 'name']);
  const siteIds = new Set<string>();
  for (const i of items) {
    const site = ref(i.target_record_id ? meters[i.target_record_id]?.site : i.proposed_values[siteA]);
    if (site) siteIds.add(site);
  }
  const [sites, codes] = await Promise.all([recordValues('site', [...siteIds], ['sentinel_building_id', 'name']), energyCodes()]);
  const building = (siteRec: string | null) => {
    if (!siteRec) return { id: null as string | null, name: null as string | null };
    const v = sites[siteRec] ?? {};
    const sid = scalar(v.sentinel_building_id);
    if (typeof sid === 'string') rememberId('building', sid, siteRec);
    return { id: typeof sid === 'string' ? sid : siteRec, name: (scalar(v.name) as string | undefined) ?? null };
  };
  return items.filter((i) => i.source_kind !== 'site').map((i): DiscoveredItem => {
    const d = i.source_display ?? {};
    const st: ItemStatus = i.absent ? (i.target_record_id ? 'not_reported' : 'closed')
      : i.status === 'ignored' ? 'ignored' : i.target_record_id ? 'linked' : i.match_state === 'suggested' ? 'matched' : 'new';
    const energyRec = ref(i.proposed_values[energyA]);
    let linked: DiscoveredItem['linked'] = null;
    let match: DiscoveredItem['match'] = null;
    if (i.target_record_id) {
      const mv = meters[i.target_record_id] ?? {};
      const smid = scalar(mv.sentinel_meter_id);
      if (typeof smid === 'string') rememberId('meter', smid, i.target_record_id);
      const b = building(ref(mv.site));
      linked = { building_id: b.id, building_name: b.name, meter_id: typeof smid === 'string' ? smid : i.target_record_id, asset_id: null, data_source_id: null };
    } else {
      const b = building(ref(i.proposed_values[siteA]));
      if (b.id) match = { building_id: b.id, building_name: b.name ?? '', method: 'nomos_proposal', score: 1, preselect: true };
    }
    return {
      external_key: i.source_id, kind: 'meter', name: String(scalar(i.proposed_values[nameA]) ?? i.source_id),
      street: first(d, ['address', 'street', 'streetName']), postcode: first(d, ['postalCode', 'postcode', 'postal_code', 'zip']),
      city: first(d, ['municipality', 'city', 'town']), energy_source: energyRec ? codes.get(energyRec) ?? null : null,
      first_data_date: null, first_data_source: null, match, status: st, linked,
      extra: { ...d, nomos_item_id: i.id },
    };
  });
}

function summary(items: DiscoveredItem[]) {
  // Tallies of the served item list (labels for the wizard), not readings.
  const by = (k: (i: DiscoveredItem) => string) => items.reduce<Record<string, number>>((a, i) => { a[k(i)] = (a[k(i)] ?? 0) + 1; return a; }, {});
  return { total: items.length, by_kind: by((i) => i.kind), by_status: by((i) => i.status) };
}

async function getConnectorDiscoverRun(connector: string, connectionId: string, runId: string, params?: { limit?: number; cursor?: string }): Promise<DiscoverRunOut> {
  await entry(connector, connectionId);
  const all = await discovered(await inbox(connectionId, runId));
  const p = page(all, { limit: params?.limit ?? 100, cursor: params?.cursor });
  return { run_id: runId, connection_id: connectionId, status: 'completed', started_at: null, completed_at: null, progress: {},
    summary: summary(all), items: p.items, next_cursor: p.next_cursor, total: all.length, error: null };
}

async function getConnectorDiscoverRunAll(connector: string, connectionId: string, runId: string): Promise<DiscoverRunOut> {
  return getConnectorDiscoverRun(connector, connectionId, runId, { limit: 1_000_000 });
}

// ---------------------------------------------------------------- link (Map review draft, approved at once)
interface NDraft { id: string; status: string; columns: { target: Array<{ attribute_id: string; slug: string }> } }

async function linkConnectorItems(connector: string, connectionId: string, items: LinkItemRequest[], dryRun = false): Promise<LinkResponse> {
  await entry(connector, connectionId);
  const useId = await grant(connectionId, ['read_meters', 'map_meter_data']);
  const before = await inbox(connectionId, useId);
  const byKey = new Map(before.filter((i) => i.source_kind !== 'site').map((i) => [i.source_id, i]));
  const results = new Map<string, LinkResult>();
  const fail = (r: LinkItemRequest, code: string, error: string) => results.set(r.external_key, {
    external_key: r.external_key, kind: r.kind, ok: false, action: null, building_id: r.building_id ?? null, meter_id: null,
    asset_id: null, data_source_id: null, energy_source: r.energy_source ?? null, counts_toward_totals: null,
    supersedes_candidates: [], error_code: code, error });

  const [sites, codes] = await Promise.all([
    resolveRecordIds('building', items.map((r) => r.building_id).filter((b): b is string => !!b)), energyCodes()]);
  const energyRec = new Map([...codes].map(([id, code]) => [code, id]));
  const chosen: Array<{ r: LinkItemRequest; item: NItem }> = [];
  for (const r of items) {
    const item = byKey.get(r.external_key);
    if (r.action === 'unlink') fail(r, 'nomos_gap', 'NomOS cannot unlink a mapped meter from its provider item');
    else if (r.kind !== 'meter') fail(r, 'nomos_gap', `NomOS maps provider meters only (not ${r.kind} items)`);
    else if (!item) fail(r, 'item_not_found', 'Not in the latest discovery');
    else if (!r.building_id || !sites.get(r.building_id)) fail(r, 'building_not_found', 'Unknown building');
    else if (r.energy_source && !energyRec.get(r.energy_source)) fail(r, 'energy_source_not_found', `Unknown energy source ${r.energy_source}`);
    else chosen.push({ r, item });
  }

  if (chosen.length) {
    const draft = data(await nomosFetch<NomosEnvelope<NDraft>>('POST', `/connections/${enc(connectionId)}/mapping`, { use_id: useId }));
    if (draft.status !== 'open') throw toAxiosError(409, { detail: 'A meter review for this connection is waiting on the Approvals page', error: { code: 'mapping_draft_locked' } }, '');
    const col = Object.fromEntries(draft.columns.target.map((c) => [c.slug, c.attribute_id]));
    const patch = (rows: Record<string, unknown>) => nomosFetch('PATCH', `/connections/${enc(connectionId)}/mapping/${enc(draft.id)}`, { rows });
    // Only what the person picked is written: every other unmapped item is set aside (ignored) in this review.
    const picked = new Set(chosen.map((c) => c.item.id));
    const others = before.filter((i) => i.source_kind !== 'site' && !i.target_record_id && !picked.has(i.id)).map((i) => i.id);
    for (let k = 0; k < others.length; k += 2000) await patch({ item_ids: others.slice(k, k + 2000), action: 'ignore' });
    for (const { r, item } of chosen) {
      const set: Record<string, unknown> = { [col.site]: { target_record_id: sites.get(r.building_id!) } };
      if (r.energy_source && col.energy_source) set[col.energy_source] = { target_record_id: energyRec.get(r.energy_source) };
      const seu = (r.details?.extra as Record<string, unknown> | undefined)?.seu;
      if (seu && col.seu_category) {
        const rec = [...(await seuCodes())].find(([, v]) => v.code === seu)?.[0];
        if (rec) set[col.seu_category] = { target_record_id: rec };
      }
      await patch({ item_ids: [item.id], set, ...(item.target_record_id ? {} : { action: 'create' }) });
    }
    if (!dryRun) {
      const submitted = data(await nomosFetch<NomosEnvelope<{ proposed_change_id: string }>>('POST', `/connections/${enc(connectionId)}/mapping/${enc(draft.id)}/submit`));
      await approve(submitted.proposed_change_id);
    }
  }

  const after = dryRun ? [] : await discovered(await inbox(connectionId, useId));
  const now = new Map(after.map((i) => [i.external_key, i]));
  for (const { r, item } of chosen) {
    const d = now.get(r.external_key);
    results.set(r.external_key, {
      external_key: r.external_key, kind: 'meter', ok: dryRun || !!d?.linked,
      action: item.target_record_id ? 'reconciled' : 'created', building_id: d?.linked?.building_id ?? r.building_id ?? null,
      meter_id: d?.linked?.meter_id ?? null, asset_id: null, data_source_id: null, energy_source: r.energy_source ?? d?.energy_source ?? null,
      counts_toward_totals: r.is_main ?? null, supersedes_candidates: [],
      error_code: dryRun || d?.linked ? null : 'not_linked', error: dryRun || d?.linked ? null : 'NomOS did not bind this item',
    });
  }
  const out = items.map((r) => results.get(r.external_key)!);
  return { dry_run: dryRun, results: out, linked: out.filter((r) => r.ok).length, failed: out.filter((r) => !r.ok).length, supersedes_candidates: [] };
}

// ---------------------------------------------------------------- sync (a load schedule, approved at once)
function syncJob(connectionId: string, l: NLoads): SyncJob | null {
  const a = l.approval ?? l.pending;
  if (!a) return null;
  const running = !!l.approval && l.loaded_meters < l.approved_meters;
  return {
    id: a.request_id, connection_id: connectionId,
    status: l.approval ? (running ? 'running' : 'completed') : 'pending',
    start_date: a.intent?.backfill_from ?? null, end_date: null,
    items_total: l.approval ? l.approved_meters : null, items_processed: l.approval ? l.loaded_meters : null,
    records_inserted: null, progress_percent: null, current_item: null, current_period: null,
    errors: l.errors.map((e) => ({ external_key: e.identity, error: e.provider_text || e.code, code: e.code })),
    started_at: zulu(a.decided_at ?? a.created_at ?? null), completed_at: null,
  };
}

// Catch-up (chosen meters from their last reading). On NomOS a meter's mark moves only after a successful write
// (meter_sync), and every run of the approved schedule reads each meter forward from its own mark, so a failing
// meter's missing days since its last reading (= its mark, which failing-meters serves as last_reading_at) are
// exactly what the next run reads. Catching meters up is therefore running the approved schedule now: clear the
// connection record's last_run_at, which the rail runs at its next tick ("never ran = due now", the same "run now"
// as an automation's). The run reads every meter of the plan from its own mark; meters already current cost one
// short read. Admin only: the connection record is admin_write and the loads status is an admin read.
async function catchUp(connectionId: string, body: SyncRequestBody): Promise<SyncJob> {
  const l = await loads(connectionId);
  if (!l.approval) {
    const why = l.pending ? 'its load schedule is waiting for approval' : 'it has no running load schedule (use Re-sync to request one)';
    throw toAxiosError(409, { detail: `Nothing to catch up on this connection: ${why}`, error: { code: 'no_running_schedule' } }, '');
  }
  if (l.blocked) throw toAxiosError(409, { detail: `NomOS cannot run this connection's load (${l.blocked})`, error: { code: l.blocked } }, '');
  const page = await queryRecords('connection', { filter: { attribute: 'connection_id', op: 'eq', value: connectionId }, limit: 1 });
  const record = page.rows[0]?.record_id;
  if (!record) throw toAxiosError(409, { detail: 'NomOS has no rail record for this connection yet', error: { code: 'connection_not_projected' } }, '');
  const res = await nomosFetch<NomosEnvelope<{ record_id?: string }>>('PATCH', `/objects/connection/records/${enc(record)}`, { values: { last_run_at: null } });
  if (!res?.data?.record_id) throw new NomosGapError('connectors: the catch-up was queued for approval instead of applied (the NomOS session is not attended)');
  const a = l.approval;
  return {
    id: a.request_id, connection_id: connectionId, status: SYNC_JOB_QUEUED,
    start_date: body.start_date ?? null, end_date: null,
    items_total: body.external_keys?.length ?? null, items_processed: null, records_inserted: null, progress_percent: null,
    current_item: null, current_period: null, errors: [], started_at: null, completed_at: null,
  };
}

async function startConnectorSync(connector: string, connectionId: string, body: SyncRequestBody = {}): Promise<SyncJob> {
  await entry(connector, connectionId);
  if (body.end_date) throw new NomosGapError('connectors: NomOS load schedules read forward continuously; an end date is not offered');
  if (body.external_keys?.length) return catchUp(connectionId, body);
  const useId = await grant(connectionId, ['read_meters', 'map_meter_data', 'load_readings']);
  const plan = data(await nomosFetch<NomosEnvelope<{ cadence: string; backfill_from: string; plan_digest: string; requestable: string | null }>>(
    'POST', `/connections/${enc(connectionId)}/loads/plan`, { use_id: useId, ...(body.start_date ? { backfill_from: body.start_date } : {}) }));
  if (plan.requestable) throw toAxiosError(409, { detail: `NomOS cannot schedule this load (${plan.requestable})`, error: { code: plan.requestable } }, '');
  const requested = data(await nomosFetch<NomosEnvelope<NApproval>>('POST', `/connections/${enc(connectionId)}/loads`, {
    request_id: crypto.randomUUID(), use_id: useId, cadence: plan.cadence, backfill_from: plan.backfill_from, plan_digest: plan.plan_digest }));
  if (requested.status === 'pending') await approve(requested.proposed_change_id);
  const job = syncJob(connectionId, await loads(connectionId));
  if (!job) throw toAxiosError(409, { detail: 'NomOS recorded no load schedule' }, '');
  return job;
}

async function getConnectorSyncStatus(connector: string, connectionId: string, params?: { limit?: number; cursor?: string }): Promise<SyncStatusOut> {
  await entry(connector, connectionId);
  const l = await loads(connectionId);
  const useId = await grant(connectionId, ['read_meters']);
  const items = (await discovered(await inbox(connectionId, useId))).filter((i) => i.status === 'linked' || i.status === 'not_reported');
  const errors = new Map(l.errors.map((e) => [e.identity, e]));
  const rows = items.map((i): ItemSyncStatus => {
    const e = errors.get(i.external_key);
    return { external_key: i.external_key, kind: i.kind, meter_id: i.linked?.meter_id ?? null,
      status: e ? 'error' : l.approval ? 'scheduled' : 'linked', start_date: l.approval?.intent?.backfill_from ?? l.history_available_from ?? null,
      first_data_source: l.history_available_from ? 'provider' : null, last_reading_at: null, error: e ? e.provider_text || e.code : null };
  });
  const p = page(rows, { limit: 1000, ...params });
  return { active_job: (() => { const j = syncJob(connectionId, l); return j && (j.status === 'running' || j.status === 'pending') ? j : null; })(),
    items: p.items, next_cursor: p.next_cursor, total: rows.length };
}


// ---------------------------------------------------------------- Eloverblik consent links (NomOS /consent-requests)
// The customer key leaves NomOS only inside consent_url, once; requests never carry it. A received consent becomes
// the eloverblik.thirdparty connection for the grantor's CVR (POST /consent-requests/{id}/connect).
function consentProvider(connector: string): string {
  const s = spec(connector);
  if (s.loginFlow !== 'consent_link') throw toAxiosError(400, { detail: `'${connector}' does not connect through a consent link`, error: { code: 'consent_not_supported' } }, '');
  return s.providers[0];
}
interface NConsent extends Omit<ConsentRequestOut, 'connector'> { provider: string }
const consentOut = (r: NConsent): ConsentRequestOut => ({
  id: r.id, connector: 'eloverblik', status: r.status, expected_grantor: r.expected_grantor ?? null, grantor: r.grantor ?? null,
  point_count: r.point_count ?? null, connection_id: r.connection_id ?? null, return_to: r.return_to ?? null, resume: r.resume ?? { mode: 'new' },
  scroll_y: r.scroll_y ?? null, created_at: zulu(r.created_at), expires_at: zulu(r.expires_at), received_at: zulu(r.received_at ?? null),
  valid_to: r.valid_to ?? null, hold_reason: r.hold_reason ?? null,
});

async function createConsentRequest(connector: string, body: ConsentRequestCreate): Promise<ConsentRequestCreated> {
  const provider = consentProvider(connector);
  const r = data(await nomosFetch<NomosEnvelope<ConsentRequestCreated>>('POST', '/consent-requests', {
    provider, expected_grantor: body.expected_grantor, return_to: body.return_to, resume: body.resume ?? {}, scroll_y: body.scroll_y ?? null }));
  return { id: r.id, consent_url: r.consent_url, expires_at: zulu(r.expires_at) };
}
async function getConsentRequest(connector: string, requestId: string): Promise<ConsentRequestOut> {
  const provider = consentProvider(connector);
  return consentOut(data(await nomosFetch<NomosEnvelope<NConsent>>('GET', `/consent-requests/${enc(requestId)}`, undefined, { query: { provider } })));
}
async function discardConsentRequest(connector: string, requestId: string): Promise<ConsentRequestOut> {
  const provider = consentProvider(connector);
  return consentOut(data(await nomosFetch<NomosEnvelope<NConsent>>('DELETE', `/consent-requests/${enc(requestId)}`, undefined, { query: { provider } })));
}
async function connectFromConsent(credentials: Record<string, unknown>, name: string | null, connectionId?: string): Promise<ConnectionOut> {
  const id = str(credentials.consent_request_id);
  if (!id) throw toAxiosError(400, { detail: 'Eloverblik connects through a consent request', error: { code: 'credentials_rejected' } }, '');
  const c = data(await nomosFetch<NomosEnvelope<NConnection>>('POST', `/consent-requests/${enc(id)}/connect`, {
    accept_grantor: !!credentials.accept_grantor, ...(name ? { name } : {}), ...(connectionId ? { connection_id: connectionId } : {}) }));
  return connectionOut(c, null);
}
async function getPlatformCredential(connector: string): Promise<PlatformCredentialOut> {
  const provider = consentProvider(connector);
  const r = data(await nomosFetch<NomosEnvelope<PlatformCredentialOut>>('GET', `/platform-credentials/${enc(provider)}`));
  return { is_set: r.is_set, expires_at: zulu(r.expires_at), expires_in_days: r.expires_in_days, third_party_id: r.third_party_id, updated_at: zulu(r.updated_at) };
}
async function setPlatformCredential(connector: string, refreshToken: string): Promise<PlatformCredentialOut> {
  const provider = consentProvider(connector);
  await nomosFetch('PUT', `/platform-credentials/${enc(provider)}`, { refresh_token: refreshToken });
  return getPlatformCredential(connector);
}
async function listConnectorAuthorizations(connector: string): Promise<AuthorizationOut[]> {
  const provider = consentProvider(connector);
  const r = await nomosFetch<NomosEnvelope<{ items: Array<Omit<AuthorizationOut, 'owner'> & { owner: { request_id: string; status: ConsentStatus; connection_id: string | null } | null }> }>>(
    'GET', `/providers/${enc(provider)}/authorizations`);
  // owner.customer_id: Sentinel's customer; on NomOS the tenant (no screen reads it).
  return r.data.items.map((a) => ({ ...a, given_at: zulu(a.given_at), owner: a.owner ? { ...a.owner, customer_id: getConfig().tenant } : null }));
}
async function assignConnectorAuthorization(connector: string, authorizationId: string, connectionId: string): Promise<ConsentRequestOut> {
  const provider = consentProvider(connector);
  return consentOut(data(await nomosFetch<NomosEnvelope<NConsent>>('POST', `/providers/${enc(provider)}/authorizations/${enc(authorizationId)}/assign`,
    { connection_id: connectionId })));
}

// ---------------------------------------------------------------- gaps (generic)
const gap = (why: string) => async (): Promise<never> => { throw new NomosGapError(`connectors: ${why}`); };

// ---------------------------------------------------------------- provider-specific wrappers
const legacy = (what: string) => gap(`${what}: legacy per-provider wizard shape that no screen calls (the ConnectorWizard replaced it); NomOS serves the unified flow`);

async function connectionsWithLoads(connector: string) { return withLoads(await connectionsOf(connector)); }
// The meter count comes from the loads status, which only an admin may read. For any other role (or when the
// loads read fails) the count is unknown, so the connection is still listed, as the member's role allows, and
// the count shows as unknown. Throwing here used to hide the provider's whole list from non-admins.
const meters = (l: NLoads | null): number => (l ? l.meters : (null as unknown as number));
const base = (c: NConnection) => ({ id: c.id, customer_id: '', created_at: zulu(c.created_at), updated_at: zulu(c.updated_at), last_sync_at: zulu(c.last_run_at ?? null) });
const st3 = (c: NConnection) => (c.credential_configured ? 'active' : 'error') as 'active' | 'error';

async function getElkraftConnections(): Promise<ElkraftConnectionListResponse> {
  const rows = await connectionsWithLoads('elkraft');
  const connections = rows.map(([c, l]): ElkraftConnection => ({ ...base(c), connection_name: c.name, kiona_username: str(c.config.username),
    top_id: c.config.top_id ? Number(c.config.top_id) : null, top_name: null, data_available_from: l?.history_available_from ?? null,
    status: st3(c), building_count: meters(l), last_error: l?.blocked ?? null }));
  return { connections, total_count: connections.length };
}
async function getDatadisConnections(): Promise<DatadisConnectionListResponse> {
  const rows = await connectionsWithLoads('datadis');
  const connections = rows.map(([c, l]): DatadisConnectionResponse => ({ ...base(c), connection_name: c.name, username: str(c.config.username) || null,
    authorized_nif: str(c.config.authorized_nif) || null, status: st3(c), cups_count: meters(l), last_error: l?.blocked ?? null, cups_list: [] }));
  return { connections, total_count: connections.length };
}
async function getEforsyningConnections(): Promise<EforsyningConnectionListResponse> {
  const rows = await connectionsWithLoads('eforsyning');
  const connections = rows.map(([c, l]): EforsyningConnectionResponse => ({ ...base(c), connection_name: c.name, supplier_slug: str(c.config.supplier),
    consumer_number: str(c.config.consumer_number), allocation_pct: c.config.allocation_pct ? Number(c.config.allocation_pct) : 100,
    status: st3(c), meter_count: meters(l), last_error: l?.blocked ?? null, meters: [] }));
  return { connections, total_count: connections.length };
}
async function getKmdConnections(): Promise<KmdConnectionListResponse> {
  const rows = await connectionsWithLoads('kmd_minforsyning');
  const connections = rows.map(([c, l]): KmdConnectionResponse => ({ ...base(c), connection_name: c.name, plant_number: Number(c.config.plant),
    utility_number: Number(c.config.utility ?? 0), email: str(c.config.email), status: st3(c), meter_count: meters(l), last_error: l?.blocked ?? null, meters: [] }));
  return { connections, total_count: connections.length };
}
async function getNoovaConnections(): Promise<NoovaConnectionListResponse> {
  const rows = await connectionsWithLoads('noova');
  const connections = rows.map(([c, l]): NoovaConnectionResponse => ({ id: c.id, name: c.name, status: st3(c), last_sync_at: zulu(c.last_run_at ?? null),
    last_error: l?.blocked ? { code: l.blocked } : null, created_at: zulu(c.created_at), noova_customer_id: str(c.config.customer_id) || null,
    username: str(c.config.username) || null, meter_count: meters(l) }));
  return { connections, total_count: connections.length };
}
/** create through the generic flow, then the provider's list shape for the new row. */
async function createVia<T>(connector: string, credentials: Record<string, unknown>, name: string | null | undefined, list: () => Promise<T[]>, id: (t: T) => string): Promise<T> {
  const created = await createConnectorConnection(connector, { credentials, name: name ?? null });
  const row = (await list()).find((t) => id(t) === created.id);
  if (!row) throw toAxiosError(500, { detail: 'Created connection not listed' }, '');
  return row;
}
const validateVia = async (connector: string, credentials: Record<string, unknown>) => validateConnectorCredentials(connector, credentials);
const creds = (connector: string) => async (connectionId: string, request: Record<string, unknown>) => updateConnectorCredentials(connector, connectionId, request);

async function validateElkraftCredentials(r: ElkraftValidateRequest): Promise<ElkraftValidateResponse> {
  if (r.session_token) throw new NomosGapError('connectors: Kiona session-token logins are not offered by NomOS elkraft.se (username + password)');
  const v = await validateVia('elkraft', { username: r.username, password: r.password });
  if (v.valid) throw new NomosGapError('connectors: validateElkraftCredentials needs Kiona top/building counts, which NomOS does not serve; use validateConnectorCredentials');
  return { valid: false, top_id: null, top_name: null, building_count: 0, data_available_from: null, error: v.error };
}

export const connectorsImpl: NomosRegistry = {
  // generic (unified /connectors)
  getConnectors, getConnectorOptions, validateConnectorCredentials, createConnectorConnection, getConnectorConnection,
  renameConnectorConnection, listConnectorIdentities,
  updateConnectorCredentials,
  restoreConnectorConnection: gap('restoring an archived login is not possible on NomOS (archiving a connection deletes its credential)'),
  createConsentRequest, getConsentRequest, discardConsentRequest,
  getPlatformCredential, setPlatformCredential, listConnectorAuthorizations, assignConnectorAuthorization,
  startConnectorDiscover, getConnectorDiscoverRun, getConnectorDiscoverRunAll, linkConnectorItems,
  startConnectorSync, getConnectorSyncStatus,

  // Shelly
  createShellyConnectionFromCredentials: (async (r: ShellyCredentialsConnectionCreate) => {
    void r; throw new NomosGapError('connectors: Shelly e-mail/password login is not offered by NomOS shelly.cloud (it needs the Cloud auth key; use the ConnectorWizard)');
  }) as AnyFn,
  discoverShellyUnits: legacy('discoverShellyUnits'), getShellyUnits: legacy('getShellyUnits'), linkShellyUnits: legacy('linkShellyUnits'),
  startShellySync: gap('Shelly history loading is not schedulable on NomOS (shelly.cloud capabilities: sync false)'),
  getShellyActiveSync: gap('Shelly history loading is not schedulable on NomOS (shelly.cloud capabilities: sync false)'),

  // Elkraft
  validateElkraftCredentials: validateElkraftCredentials as AnyFn,
  createElkraftConnection: (async (r: ElkraftConnectionCreate) => {
    if (r.session_token) throw new NomosGapError('connectors: Kiona session-token logins are not offered by NomOS elkraft.se');
    return createVia('elkraft', { username: r.username, password: r.password }, r.connection_name, async () => (await getElkraftConnections()).connections, (c) => c.id);
  }) as AnyFn,
  getElkraftConnections,
  discoverElkraftHierarchy: legacy('discoverElkraftHierarchy'), getElkraftHierarchy: legacy('getElkraftHierarchy'),
  bulkLinkElkraftBuildings: legacy('bulkLinkElkraftBuildings'),
  startElkraftSync: legacy('startElkraftSync (per-building job counts)'), getElkraftActiveSync: legacy('getElkraftActiveSync (per-building job counts)'),
  updateElkraftCredentials: (async (id: string, r: { username: string; password: string }) => creds('elkraft')(id, { username: r.username, password: r.password })) as AnyFn,

  // Datadis
  validateDatadisCredentials: (async (r: DatadisValidateRequest): Promise<DatadisValidateResponse> => {
    const v = await validateVia('datadis', { username: r.username, password: r.password });
    if (v.valid) throw new NomosGapError('connectors: validateDatadisCredentials needs the CUPS count, which a NomOS login check does not read; use validateConnectorCredentials');
    return { valid: false, cups_count: 0, error: v.error };
  }) as AnyFn,
  createDatadisConnection: (async (r: DatadisConnectionCreate) => createVia('datadis',
    { username: r.username, password: r.password, authorized_nif: r.authorized_nif ?? '' }, r.connection_name,
    async () => (await getDatadisConnections()).connections, (c) => c.id)) as AnyFn,
  discoverDatadisCUPS: legacy('discoverDatadisCUPS'), linkDatadisCUPS: legacy('linkDatadisCUPS'), bulkLinkDatadisCUPS: legacy('bulkLinkDatadisCUPS'),
  startDatadisSync: legacy('startDatadisSync (per-CUPS job counts)'), getDatadisActiveSync: legacy('getDatadisActiveSync (per-CUPS job counts)'),
  updateDatadisCredentials: (async (id: string, r: { username: string; password: string }) => creds('datadis')(id, { username: r.username, password: r.password })) as AnyFn,

  // eForsyning
  getEforsyningUtilities: (async (): Promise<EforsyningUtilitiesResponse> => {
    const opts = await getConnectorOptions('eforsyning', 'supplier');
    const utilities = opts.map((o) => ({ slug: o.value, label: o.label, api_server_url: (o.extra?.api_server_url as string | null) ?? null }));
    return { utilities, total_count: utilities.length };
  }) as AnyFn,
  validateEforsyningCredentials: (async (r: { supplier_slug: string; consumer_number: string; pin_code: string; api_server_url?: string }): Promise<EforsyningValidateResponse> => {
    const v = await validateVia('eforsyning', { supplier: r.supplier_slug, consumer_number: r.consumer_number, pin_code: r.pin_code, api_server_url: r.api_server_url ?? '' });
    return { valid: v.valid, api_server_url: r.api_server_url ?? null, error: v.error };
  }) as AnyFn,
  createEforsyningConnection: (async (r: EforsyningConnectionCreate) => createVia('eforsyning', {
    supplier: r.supplier_slug, consumer_number: r.consumer_number, pin_code: r.pin_code, api_server_url: r.api_server_url ?? '',
    asset_id: r.asset_id ?? '', installation_id: r.installation_id ?? '', allocation_pct: r.allocation_pct ?? '' }, r.connection_name,
  async () => (await getEforsyningConnections()).connections, (c) => c.id)) as AnyFn,
  discoverEforsyningMeters: legacy('discoverEforsyningMeters'), linkEforsyningMeter: legacy('linkEforsyningMeter'),
  startEforsyningSync: legacy('startEforsyningSync (per-meter job counts)'), getEforsyningActiveSync: legacy('getEforsyningActiveSync (per-meter job counts)'),
  updateEforsyningCredentials: (async (id: string, r: { pin_code: string }) => creds('eforsyning')(id, { pin_code: r.pin_code })) as AnyFn,

  // Noova
  validateNoovaCredentials: (async (r: { username: string; password: string; customer_id: string }): Promise<NoovaValidateResponse> => {
    const v = await validateVia('noova', r);
    if (v.valid) throw new NomosGapError('connectors: validateNoovaCredentials needs Noova asset/meter counts, which a NomOS login check does not read; use validateConnectorCredentials');
    return { valid: false, error: v.error, asset_count: 0, electricity_meter_count: 0 };
  }) as AnyFn,
  createNoovaConnection: (async (r: NoovaConnectionCreate) => createVia('noova', { username: r.username, password: r.password, customer_id: r.customer_id }, r.name,
    async () => (await getNoovaConnections()).connections, (c) => c.id)) as AnyFn,
  updateNoovaCredentials: (async (id: string, r: { username: string; password: string }) => creds('noova')(id, { username: r.username, password: r.password })) as AnyFn,
  discoverNoova: legacy('discoverNoova (JYSK store-code report)'), linkNoova: legacy('linkNoova'),
  startNoovaSync: legacy('startNoovaSync'),

  // KMD MinForsyning
  getKmdPlants: (async (): Promise<KmdPlantsResponse> => {
    const opts = await getConnectorOptions('kmd_minforsyning', 'plant');
    return { plants: opts.map((o) => ({ plant_number: Number(o.extra?.plant_number), utility_number: Number(o.extra?.utility_number ?? 0),
      name: o.label.replace(/ \(plant \d+\)$/, ''), city: null })) };
  }) as AnyFn,
  validateKmdCredentials: (async (r: KmdValidateRequest): Promise<KmdValidateResponse> => {
    const v = await validateVia('kmd_minforsyning', { plant: `${r.plant_number}:${r.utility_number}`, email: r.email, password: r.password });
    if (v.valid) throw new NomosGapError('connectors: validateKmdCredentials needs the meter count, which a NomOS login check does not read; use validateConnectorCredentials');
    return { valid: false, meter_count: 0, error: v.error };
  }) as AnyFn,
  createKmdConnection: (async (r: KmdConnectionCreate) => createVia('kmd_minforsyning',
    { plant: `${r.plant_number}:${r.utility_number}`, email: r.email, password: r.password }, r.connection_name,
    async () => (await getKmdConnections()).connections, (c) => c.id)) as AnyFn,
  discoverKmdMeters: legacy('discoverKmdMeters'), linkKmdMeter: legacy('linkKmdMeter'),
  startKmdSync: legacy('startKmdSync (per-meter job counts)'), getKmdActiveSync: legacy('getKmdActiveSync (per-meter job counts)'),
  updateKmdCredentials: (async (id: string, r: { email: string; password: string }) => creds('kmd_minforsyning')(id, { email: r.email, password: r.password })) as AnyFn,
};
